import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Search } from 'lucide-react';
import { useStockAdjustmentStore, useProductStore } from '../../hooks';
import { Button, Card, CardContent, Badge } from '@lancar-pos/ui';

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'danger'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
};

const statusLabels: Record<string, string> = {
  pending: 'Menunggu',
  approved: 'Disetujui',
  rejected: 'Ditolak',
};

const reasonLabels: Record<string, string> = {
  stock_opname: 'Stock Opname',
  damage: 'Kerusakan',
  loss: 'Hilang',
  found: 'Ditemukan',
  correction: 'Koreksi',
  other: 'Lainnya',
};

export function StockAdjustmentsPage() {
  const navigate = useNavigate();
  const { stockAdjustments } = useStockAdjustmentStore();
  const { products } = useProductStore();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredAdjustments = useMemo(() => {
    return stockAdjustments
      .filter((sa) => !sa._deleted)
      .filter((sa) => {
        if (typeFilter !== 'all' && sa.adjustmentType !== typeFilter) return false;
        if (statusFilter !== 'all' && sa.status !== statusFilter) return false;
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const product = products.find((p) => p.id === sa.productId);
        return product?.name.toLowerCase().includes(query);
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [stockAdjustments, typeFilter, statusFilter, searchQuery, products]);

  const getProductName = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    return product?.name || '-';
  };

  const handleAddAdjustment = () => {
    window.location.href = '/app/inventory/stock-adjustments/new';
  };

  const handleViewAdjustment = (id: string) => {
    window.location.href = `/app/inventory/stock-adjustments/${id}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate({ to: '/app' })}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Penyesuaian Stok</h1>
          </div>
          <Button onClick={handleAddAdjustment}>
            <Plus className="w-4 h-4 mr-2" />Penyesuaian Baru
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 lg:p-6 space-y-4">
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cari produk..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <div className="flex gap-1">
                {['all', 'increase', 'decrease'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setTypeFilter(type)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      typeFilter === type
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {type === 'all' ? 'Semua' : type === 'increase' ? 'Penambahan' : 'Pengurangan'}
                  </button>
                ))}
              </div>
              <div className="flex gap-1">
                {['all', 'pending', 'approved', 'rejected'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      statusFilter === status
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {status === 'all' ? 'Semua' : statusLabels[status]}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produk</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipe</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Jumlah</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alasan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAdjustments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        {searchQuery || typeFilter !== 'all' || statusFilter !== 'all' 
                          ? 'Penyesuaian tidak ditemukan' 
                          : 'Belum ada penyesuaian stok'}
                      </td>
                    </tr>
                  ) : (
                    filteredAdjustments.map((adjustment) => (
                      <tr 
                        key={adjustment.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewAdjustment(adjustment.id)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {new Date(adjustment.createdAt).toLocaleDateString('id-ID')}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {getProductName(adjustment.productId)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-medium ${
                            adjustment.adjustmentType === 'increase' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {adjustment.adjustmentType === 'increase' ? '+' : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                          {adjustment.quantity}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {reasonLabels[adjustment.reason] || adjustment.reason}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={statusColors[adjustment.status]}>
                            {statusLabels[adjustment.status]}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewAdjustment(adjustment.id);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                          >
                            <Search className="w-4 h-4 text-gray-600" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
